import React, { useEffect, useState } from "react";
import {
  collection, query, where,
  getDocs, onSnapshot, doc, getDoc,
} from "firebase/firestore";
import { firestore } from "../shared/FirebaseConfig";
import { useUser } from "../shared/UserContext";
import { studentGrade } from "../shared/gradeModel";
import { displayName } from "../shared/nameSort";
import useTripsToday from "../hooks/useTripsToday";
import ReturnIdPrompt from "./ReturnIdPrompt";
import { SIGNOUTS, createSignOut, signInOne, signInMany } from "./signoutData";
import toiletIcon from "../../images/toilet.png";
import waterFountainIcon from "../../images/waterfountain.png";
import nurseIcon from "../../images/nurse.png";
import officeIcon from "../../images/office.png";
import libraryIcon from "../../images/book.png";
import questionIcon from "../../images/question.png";
import "../student/StudentSignOut.css";
import "./Attendance.css";
import { byLastName } from "../shared/nameSort";

const LOCATIONS = [
  { name: "Bathroom",      icon: toiletIcon },
  { name: "Water Fountain", icon: waterFountainIcon },
  { name: "Nurse",         icon: nurseIcon },
  { name: "Library",       icon: libraryIcon },
  { name: "Office",        icon: officeIcon },
  { name: "Other",         icon: questionIcon },
];

function getElapsed(ts, now) {
  const mins = Math.floor((now - new Date(ts)) / 60000);
  if (mins < 1) return "just now";
  if (mins === 1) return "1 min";
  return `${mins} min`;
}

function LocationPicker({ student, classId, tripsToday = 0, onDone, onCancel }) {
  const { user, schoolId, teacherName, allowCustomLocation } = useUser();
  const [showCustom, setShowCustom] = useState(false);
  const [custom, setCustom] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const signOut = async (location) => {
    setSubmitting(true);
    setError("");
    try {
      // Denormalize class name + room onto the record so boards read sign-outs directly.
      const classData = (await getDoc(doc(firestore, "classes", classId))).data() || {};
      await createSignOut({
        studentDocumentNumber: student.id,
        studentName: student.studentName,
        idNumber: student.studentId || "",
        grade: studentGrade(student) || "",
        schoolId: student.schoolId || schoolId || "",
        teacherUID: user.uid,
        teacherName,
        classId,
        className: classData.className ?? "",
        homeroom: classData.className ?? "",
        room: classData.roomNumber ?? "",
        time_out: new Date().toISOString(),
        location,
      });
      onDone();
    } catch (err) {
      console.error("Error signing out:", err);
      setError("Something went wrong. Try again.");
      setSubmitting(false);
    }
  };

  const handleLocationClick = (name) => {
    if (name === "Other" && allowCustomLocation) {
      setShowCustom(true);
    } else {
      signOut(name);
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0,
      background: "rgba(0,0,0,0.45)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1000,
    }}>
      <div style={{
        background: "#fff", borderRadius: 20, padding: "26px 28px",
        maxWidth: 560, width: "95%", textAlign: "center",
        boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
        maxHeight: "90vh", overflowY: "auto",
      }}>
        <h2 style={{ margin: "0 0 6px", fontSize: 22 }}>{displayName(student)}</h2>
        {tripsToday > 0 && (
          <p style={{
            margin: "0 0 10px", fontSize: 14, fontWeight: 600,
            color: tripsToday >= 3 ? "#b4231f" : "#a35a00",
            background: tripsToday >= 3 ? "#fdeef0" : "#fff4e0",
            display: "inline-block", padding: "5px 12px", borderRadius: 999,
          }}>
            🔁 Already out {tripsToday} time{tripsToday === 1 ? "" : "s"} today
          </p>
        )}
        <p style={{ color: "var(--color-secondary)", marginBottom: 14, fontSize: 15 }}>
          Where are you going?
        </p>

        {!showCustom ? (
          <div className="button-group ng-locations" style={{ marginBottom: 16 }}>
            {LOCATIONS.map(({ name, icon }) => (
              <button
                key={name}
                className="location-button"
                onClick={() => handleLocationClick(name)}
                disabled={submitting}
              >
                <img src={icon} alt={name} className="location-icon" />
                {name}
              </button>
            ))}
          </div>
        ) : (
          <div style={{ marginBottom: 20 }}>
            <input
              type="text"
              placeholder="Enter destination…"
              value={custom}
              onChange={e => setCustom(e.target.value)}
              onKeyDown={e => e.key === "Enter" && custom.trim() && signOut(custom.trim())}
              autoFocus
              style={{ width: "100%", padding: "11px 14px", fontSize: 15, borderRadius: 8, border: "1px solid #ddd", boxSizing: "border-box", marginBottom: 10 }}
            />
            <button
              onClick={() => signOut(custom.trim())}
              disabled={!custom.trim() || submitting}
              style={{ width: "100%", padding: "14px", fontSize: 15, borderRadius: 12, fontWeight: 500, marginBottom: 8 }}
            >
              Go to {custom || "…"}
            </button>
            <button
              onClick={() => setShowCustom(false)}
              style={{ background: "none", border: "none", color: "var(--color-secondary)", fontSize: 13, cursor: "pointer" }}
            >
              ← Back
            </button>
          </div>
        )}

        {error && <p style={{ color: "red", marginBottom: 10, fontSize: 14 }}>{error}</p>}

        <button
          onClick={onCancel}
          style={{
            background: "none", border: "1px solid #e0e0e0",
            color: "var(--color-secondary)", padding: "9px 28px",
            borderRadius: 8, cursor: "pointer", fontSize: 14,
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export default function NameSignOutGrid({ classId, requireIdOnReturn }) {
  const { schoolId, uid, showTripsCount } = useUser();
  // When the teacher hides the count, disable the hook entirely (empty result →
  // no 🔁 badges anywhere on the grid or the location picker).
  const tripsToday = useTripsToday(showTripsCount ? uid : null);
  const [students, setStudents] = useState([]);
  // Map: studentDocId → { id: signoutDocId, location, time_out }
  const [signedOutMap, setSignedOutMap] = useState(new Map());
  const [selected, setSelected] = useState(null);
  const [returnPrompt, setReturnPrompt] = useState(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!schoolId) return;
    // schoolId scopes both list queries so the security rules can authorize them.
    getDocs(query(
      collection(firestore, "students"),
      where("schoolId", "==", schoolId),
      where("enrolledClasses", "array-contains", classId)
    )).then(snap => {
      const list = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort(byLastName);
      setStudents(list);
    });
  }, [classId, schoolId]);

  useEffect(() => {
    if (!schoolId) return;
    return onSnapshot(
      query(collection(firestore, SIGNOUTS), where("schoolId", "==", schoolId), where("classId", "==", classId)),
      snap => {
        const map = new Map();
        snap.docs.forEach(d => {
          const data = d.data();
          map.set(data.studentDocumentNumber, {
            id: d.id,
            location: data.location,
            time_out: data.time_out,
          });
        });
        setSignedOutMap(map);
      }
    );
  }, [classId, schoolId]);

  const handleSignIn = async (studentDocId) => {
    const signout = signedOutMap.get(studentDocId);
    if (!signout) return;
    try {
      await signInOne(signout.id);
    } catch (err) {
      console.error("Error signing in:", err);
    }
  };

  // 9876 override — sign in everyone currently out in this class
  const handleSignInAll = async () => {
    try {
      await signInMany([...signedOutMap.values()].map(s => s.id));
    } catch (err) {
      console.error("Error signing in all:", err);
    }
  };

  return (
    <div className="student-grid">
      {students.map(student => {
        const signout = signedOutMap.get(student.id);
        const isOut = !!signout;
        const mins = isOut ? Math.floor((now - new Date(signout.time_out)) / 60000) : 0;
        const isLong = mins >= 10;
        // Completed trips today + the current one if they're out right now.
        const completedToday = tripsToday.byDocId[student.id] || 0;
        const totalToday = completedToday + (isOut ? 1 : 0);

        if (isOut) {
          const handleCardClick = () => {
            if (requireIdOnReturn && student.studentId) {
              setReturnPrompt(student);
            } else {
              handleSignIn(student.id);
            }
          };
          return (
            <div
              key={student.id}
              onClick={handleCardClick}
              style={{
                border: `1px solid ${isLong ? "#b45309" : "var(--color-primary)"}`,
                borderRadius: 8,
                background: isLong ? "#d97706" : "var(--color-primary)",
                color: "#fff",
                minWidth: 220,
                minHeight: 75,
                padding: 10,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "transform 0.2s ease",
                textAlign: "center",
              }}
            >
              <span style={{ fontWeight: "bold", fontSize: 14 }}>{displayName(student)}</span>
              <span style={{
                fontSize: 12,
                marginTop: 5,
                color: "rgba(255,255,255,0.85)",
                fontWeight: 500,
              }}>
                {signout.location} · {getElapsed(signout.time_out, now)}
              </span>
              {totalToday >= 2 && (
                <span style={{
                  marginTop: 5, fontSize: 11, fontWeight: 700,
                  background: "rgba(255,255,255,0.22)", color: "#fff",
                  padding: "1px 8px", borderRadius: 999,
                }}>
                  🔁 {totalToday}× today
                </span>
              )}
            </div>
          );
        }

        return (
          <div
            key={student.id}
            className="student-item not-present"
            onClick={() => setSelected(student)}
            style={{ position: "relative" }}
          >
            <span className="student-name">{displayName(student)}</span>
            {completedToday > 0 && (
              <span
                title={`Signed out ${completedToday} time${completedToday === 1 ? "" : "s"} already today`}
                style={{
                  position: "absolute", top: 4, right: 6,
                  fontSize: 11, fontWeight: 700,
                  color: completedToday >= 3 ? "#b4231f" : "#a35a00",
                  background: completedToday >= 3 ? "#fdeef0" : "#fff4e0",
                  padding: "1px 7px", borderRadius: 999,
                }}
              >
                🔁 {completedToday}
              </span>
            )}
          </div>
        );
      })}

      {selected && (
        <LocationPicker
          student={selected}
          classId={classId}
          tripsToday={tripsToday.byDocId[selected.id] || 0}
          onDone={() => setSelected(null)}
          onCancel={() => setSelected(null)}
        />
      )}

      {returnPrompt && (
        <ReturnIdPrompt
          studentName={returnPrompt.studentName}
          expectedId={returnPrompt.studentId}
          onConfirm={() => { handleSignIn(returnPrompt.id); setReturnPrompt(null); }}
          onConfirmAll={() => { handleSignInAll(); setReturnPrompt(null); }}
          onCancel={() => setReturnPrompt(null)}
        />
      )}
    </div>
  );
}
